import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { Box, Chip, Container, MenuItem } from '@mui/material';
import { DataGrid, GridColDef, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { MemoizedColumnHeaders, MemoizedRow } from 'components/mui/Memoized';
import useContextMenu from 'hooks/useContextMenu';
import { CarModel, StockStatus } from 'models/StockModel';
import { CompanyType } from 'models/CompanyModel';
import { PartyModel, PartyType } from 'models/PartyModel';
import usePartyModals from 'modules/parties/hooks/usePartyModals';
import ProblemMark from 'modules/cars/common/ProblemMark';
import CopyableChassis from 'modules/cars/common/CopyableChassis';
import VoyageLink from 'modules/cars/common/VoyageLink';
import useSyncCars from 'modules/cars/hooks/useSyncCars';
import useCars from 'modules/cars/hooks/useCars';
import auth from 'recoil/atoms/auth';


const carStatuses = Object.values(StockStatus);

const CarsInner = () => {
    const {id} = useParams();
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const [cars, setCars] = useState<CarModel[]>([]);
    const {findCars} = useCars();
    const {openAddPartyModal, openEditPartyModal, confirmDetachParty} = usePartyModals();
    const navigate = useNavigate();

    useSyncCars(setCars);

    const {onContextMenu: openConsignee} = useContextMenu<CarModel>(
        ({value, close}) => partyMenuBuilder(PartyType.Consignee, value.parties.consignee, value, close)
    );

    const {onContextMenu: openNotify} = useContextMenu<CarModel>(
        ({value, close}) => partyMenuBuilder(PartyType.Notify, value.parties.notify, value, close)
    );

    const {onContextMenu: openDealer} = useContextMenu<CarModel>(
        ({value, close}) => partyMenuBuilder(PartyType.Dealer, value.parties.dealer, value, close)
    );

    useEffect(() => {
        findCars({parties: [Number(id)]})
            .then(res => setCars(res.data));
        // eslint-disable-next-line
    }, [id]);

    const partyMenuBuilder = (type: PartyType, party: PartyModel | undefined, car: CarModel, close: () => void) => {
        if (party) {
            return [
                !(type === PartyType.Dealer && my.type === CompanyType.Shipper) && <MenuItem
                    key="edit"
                    onClick={() => {
                        openEditPartyModal(party);
                        close();
                    }}>
                    {t('edit_title', {value: t(type)})}
                </MenuItem>,
                (car.status === StockStatus.Pending || my.type === CompanyType.Master) && <MenuItem
                    key="detach"
                    onClick={() => {
                        confirmDetachParty('car', car.id, party)
                        close();
                    }}
                >
                    {t('detach')}
                </MenuItem>,

                <MenuItem
                    key="cars"
                    onClick={() => {
                        navigate(`/cars/party/${party.id}`);
                        close();
                    }}
                >
                    {t('cars')}
                </MenuItem>,
            ];
        }

        return [
            <MenuItem
                key="add"
                onClick={() => {
                    openAddPartyModal('car', car.id, type, Object.values(car.parties).map(p => p?.email));
                    close();
                }}>
                Add {type}
            </MenuItem>
        ];
    }

    if (!cars.length) {
        return null;
    }

    const columns: GridColDef<CarModel>[] = [
        ...my.type === CompanyType.Master ? [{
            field: 'shipper',
            headerName: t('shipper'),
            valueGetter: params => params.row.company.name,
            flex: 1,
            minWidth: 120,
        }] as GridColDef<CarModel>[] : [],
        {
            field: 'status',
            headerName: t('status'),
            type: 'singleSelect',
            valueOptions: carStatuses,
            renderCell: params => <Chip
                label={params.value}
                color={colors[params.value as StockStatus] || undefined}
                variant={params.value === StockStatus.Shipped ? 'outlined' : undefined}
                size="small"
            />,
            width: 100,
        },
        {
            field: 'model',
            headerName: t('model'),
            renderCell: params => <span>
                {params.row.make} <ProblemMark car={params.row}/><br/>
                {params.row.model}
            </span>,
            flex: 1,
        },
        {
            field: 'chassisNumber',
            headerName: t('chassis'),
            renderCell: params => <CopyableChassis car={params.row}/>,
            width: 210,
        },
        {
            field: 'voyage',
            headerName: t('voyage'),
            valueGetter: params => params.row.voyage?.number,
            renderCell: params => params.row.voyage && <VoyageLink voyage={params.row.voyage}/>,
            width: 130,
        },
        {
            field: 'consignee',
            headerName: t('consignee'),
            valueGetter: params => params.row.parties.consignee?.name,
            renderCell: params => <Box
                sx={{width: "100%"}}
                onContextMenu={openConsignee(params.row)}
            >
                {params.value || <>&nbsp;</>}
            </Box>,
            flex: 1,
        },
        {
            field: 'notify',
            headerName: t('notify'),
            valueGetter: params => params.row.parties.notify?.name,
            renderCell: params => <Box
                sx={{width: "100%"}}
                onContextMenu={openNotify(params.row)}
            >
                {params.value || <>&nbsp;</>}
            </Box>,
            flex: 1,
        },
        {
            field: 'dealer',
            headerName: t('dealer'),
            valueGetter: params => params.row.parties.dealer?.shortName,
            renderCell: params => <Box
                sx={{width: '100%'}}
                onContextMenu={openDealer(params.row)}
            >
                {params.value || <>&nbsp;</>}
            </Box>,
            flex: 1,
        },
    ];

    return (
        <Container maxWidth={false} sx={{mt: 2}}>
            <Box sx={{height: 'calc(100vh - 75px)', bgcolor: 'background.paper'}}>
                <DataGrid
                    pageSizeOptions={[100]}
                    rows={cars}
                    columns={columns}
                    slots={{
                        row: MemoizedRow,
                        columnHeaders: MemoizedColumnHeaders,
                        toolbar: CustomToolbar,
                    }}
                    disableRowSelectionOnClick
                />
            </Box>
        </Container>
    );
};


const colors: Record<StockStatus, React.ComponentProps<typeof Chip>['color']> = {
    pending: undefined,
    ready: 'info',
    shipped: 'success',
    received: 'success',
    canceled: 'error',
    lost: 'error'
};


const CustomToolbar = () => {
    return (
        <GridToolbarContainer>
            <GridToolbarExport
                printOptions={{ disableToolbarButton: true }}
            />
        </GridToolbarContainer>
    );
}

const CarsByStatus = () => {
    return (
        <Suspense fallback={null}>
            <CarsInner/>
        </Suspense>
    )
}

export default CarsByStatus;
